import { type PointerEvent } from 'react';

import { useOverlay } from '../../core/components/overlay-tooltip/contexts/OverlayContext.js';
import { useTooltip } from '../../core/components/overlay-tooltip/contexts/TooltipContext.js';
import type { Data } from '../../core/components/overlay-tooltip/types.js';
import { useTooltipReducer } from '../../core/components/overlay-tooltip/useTooltipReducer.js';
import { emptyData } from '../../core/components/overlay-tooltip/utils.js';
import { useGaugeChartTooltipName } from '../contexts/GaugeChartTooltipNameContext.js';

interface UseGaugeChartOverlayEventsOptions {
  value: number;
  color: string;
}

interface UseGaugeChartOverlayEventsReturn {
  onPointerMove: (event: PointerEvent<HTMLDivElement>) => void;
  onClick: (event: PointerEvent<HTMLDivElement>) => void;
  onPointerLeave: () => void;
}

export const useGaugeChartOverlayEvents = (
  options: UseGaugeChartOverlayEventsOptions,
): UseGaugeChartOverlayEventsReturn => {
  const { value, color } = options;
  const tooltipName = useGaugeChartTooltipName();
  const overlay = useOverlay();
  const tooltip = useTooltip();
  const dispatch = useTooltipReducer();

  const data: Data = {
    geometry: 'meterbar',
    series: [{ name: tooltipName ?? 'Value', value, color }],
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    dispatch({ type: 'SET_POSITION', payload: data })(event);
  };

  const onClick = (event: PointerEvent<HTMLDivElement>) => {
    if (tooltip.getState().pinned) {
      dispatch({
        type: 'SET_PINNED',
        payload: { pinned: false, data: emptyData() },
      })();
    } else {
      const bounds = overlay.getBounds();
      if (!bounds) {
        return;
      }
      const relativeX = Math.round(event.clientX - bounds.left);
      const relativeY = Math.round(event.clientY - bounds.top);
      dispatch({
        type: 'SET_PINNED',
        payload: { pinned: true, relativeX, relativeY, data },
      })();
    }
  };

  const onPointerLeave = dispatch({ type: 'SET_OUT_BOUNDS' });

  return {
    onPointerMove,
    onClick,
    onPointerLeave,
  };
};
